// Google Analytics 4 configuration
import { logger } from '@/lib/logger';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export const GA_TRACKING_ID = import.meta.env.VITE_GA_ID || 'G-RCJJMTD9N4';

// Flag to prevent multiple initializations
let gaLoaded = false;
let gaLoading = false;

/**
 * Loads Google Analytics dynamically.
 * Injects gtag.js script and initializes GA4 with privacy settings.
 * Prevents multiple initializations.
 * 
 * @param trackingId - Google Analytics tracking ID (defaults to GA_TRACKING_ID)
 */
export const loadGoogleAnalytics = (trackingId: string = GA_TRACKING_ID): void => {
  // Safety checks
  if (typeof window === 'undefined' || !trackingId) {
    console.warn('[GA] Cannot load Google Analytics: window undefined or no tracking ID');
    return;
  }

  // Prevent multiple initializations
  if (gaLoaded) {
    return;
  }

  if (gaLoading) {
    return;
  }

  const existingScript = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
  if (existingScript) {
    gaLoaded = true;
    return;
  }

  gaLoading = true;

  try {
    // Initialize dataLayer before loading script
    window.dataLayer = window.dataLayer || [];
    
    // Define gtag function
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Load gtag.js script dynamically
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    
    script.onload = () => {
      // Initialize GA4 with privacy settings
      window.gtag('js', new Date());
      window.gtag('config', trackingId, {
        anonymize_ip: true,
        send_page_view: false
      });
      
      gaLoaded = true;
      gaLoading = false;
    };

    script.onerror = () => {
      logger.error('[GA] Failed to load Google Analytics script');
      gaLoading = false;
    };

    document.head.appendChild(script);
  } catch (error) {
    logger.error('[GA] Error loading Google Analytics:', error);
    gaLoading = false;
  }
};

/**
 * Removes Google Analytics script from DOM
 */
export const removeGoogleAnalytics = (): void => {
  if (typeof window === 'undefined') return;

  // Check if GA was actually loaded before removing
  const script = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
  const wasLoaded = gaLoaded || !!script;

  // Remove gtag.js script
  if (script) {
    script.remove();
  }

  // Clear dataLayer
  if (window.dataLayer) {
    window.dataLayer = [];
  }

  // Remove gtag function
  if (window.gtag) {
    delete (window as any).gtag;
  }

  gaLoaded = false;
  gaLoading = false;
};

// Initialize Google Analytics (legacy function - kept for backward compatibility)
export const initGA = (trackingId: string) => {
  loadGoogleAnalytics(trackingId);
};

/**
 * Track page view manually (for SPA routing).
 * Only tracks if GA is loaded and consent is granted.
 * 
 * @param url - Page URL/path to track
 * @param title - Optional page title
 * @param trackingId - Optional tracking ID (defaults to GA_TRACKING_ID)
 */
export const trackPageView = (url: string, title?: string, trackingId: string = GA_TRACKING_ID): void => {
  // Safety checks
  if (typeof window === 'undefined') {
    return;
  }

  if (!trackingId) {
    logger.warn('[GA] Cannot track page view: no tracking ID');
    return;
  }

  if (!window.gtag) {
    logger.debug('[GA] Cannot track page view: gtag not loaded');
    return;
  }

  if (!gaLoaded) {
    logger.debug('[GA] Cannot track page view: GA not loaded yet');
    return;
  }

  try {
    // Extract path from URL if full URL is provided
    const pagePath = url.startsWith('http') ? new URL(url).pathname : url;
    
    window.gtag('config', trackingId, {
      page_path: pagePath,
      page_title: title || document.title,
    });
  } catch (error) {
    logger.error('[GA] Error tracking page view:', error);
  }
};

/**
 * Track custom events.
 * Only tracks if GA is loaded.
 * 
 * @param eventName - Event name
 * @param eventParams - Event parameters
 * @param trackingId - Optional tracking ID (defaults to GA_TRACKING_ID)
 */
export const trackEvent = (eventName: string, eventParams: Record<string, any>, trackingId: string = GA_TRACKING_ID): void => {
  // Safety checks
  if (typeof window === 'undefined') {
    return;
  }

  if (!trackingId) {
    console.warn('[GA] Cannot track event: no tracking ID');
    return;
  }

  if (!window.gtag) {
    console.warn('[GA] Cannot track event: gtag not loaded');
    return;
  }

  if (!gaLoaded) {
    console.warn('[GA] Cannot track event: GA not loaded yet');
    return;
  }

  try {
    window.gtag('event', eventName, eventParams);
  } catch (error) {
    logger.error('[GA] Error tracking event:', error);
  }
};

// Track user engagement
export const trackEngagement = (action: string, category: string, label?: string, value?: number) => {
  trackEvent(action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

// Track form submissions
export const trackFormSubmission = (formName: string, success: boolean = true) => {
  trackEvent('form_submit', {
    form_name: formName,
    success: success,
  });
};

// Track button clicks
export const trackButtonClick = (buttonName: string, location: string) => {
  trackEvent('button_click', {
    button_name: buttonName,
    location: location,
  });
};

// Track search queries
export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
  });
};

// Track file downloads
export const trackDownload = (fileName: string, fileType: string) => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
  });
};

// Track video interactions
export const trackVideoInteraction = (action: string, videoTitle: string, progress?: number) => {
  trackEvent('video_interaction', {
    video_action: action,
    video_title: videoTitle,
    video_progress: progress,
  });
};

// Track e-commerce events
export const trackPurchase = (transactionId: string, value: number, currency: string = 'RON') => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency,
  });
};

// Track appointment bookings
export const trackAppointmentBooking = (serviceName: string, appointmentValue: number) => {
  trackEvent('appointment_booking', {
    service_name: serviceName,
    appointment_value: appointmentValue,
    currency: 'RON',
  });
};

// Track user properties
export const setUserProperties = (properties: Record<string, any>) => {
  if (GA_TRACKING_ID && typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      custom_map: properties,
    });
  }
};

// Track conversion goals
export const trackConversion = (goalName: string, value?: number) => {
  trackEvent('conversion', {
    goal_name: goalName,
    value: value,
  });
};

// Enhanced e-commerce tracking
export const trackEnhancedEcommerce = {
  // View item
  viewItem: (itemId: string, itemName: string, category: string, value: number) => {
    trackEvent('view_item', {
      currency: 'RON',
      value: value,
      items: [{
        item_id: itemId,
        item_name: itemName,
        category: category,
        price: value,
        quantity: 1,
      }],
    });
  },

  // Add to cart
  addToCart: (itemId: string, itemName: string, category: string, value: number) => {
    trackEvent('add_to_cart', {
      currency: 'RON',
      value: value,
      items: [{
        item_id: itemId,
        item_name: itemName,
        category: category,
        price: value,
        quantity: 1,
      }],
    });
  },

  // Begin checkout
  beginCheckout: (items: Array<{
    item_id: string;
    item_name: string;
    category: string;
    price: number;
    quantity: number;
  }>) => {
    const totalValue = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    trackEvent('begin_checkout', {
      currency: 'RON',
      value: totalValue,
      items: items,
    });
  },

  // Purchase
  purchase: (transactionId: string, items: Array<{
    item_id: string;
    item_name: string;
    category: string;
    price: number;
    quantity: number;
  }>) => {
    const totalValue = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    trackEvent('purchase', {
      transaction_id: transactionId,
      currency: 'RON',
      value: totalValue,
      items: items,
    });
  },
};

// Performance tracking
export const trackPerformance = {
  // Page load time
  pageLoadTime: (loadTime: number) => {
    trackEvent('page_load_time', {
      load_time: loadTime,
    });
  },

  // Core Web Vitals
  coreWebVitals: (metric: string, value: number, rating: string) => {
    trackEvent('core_web_vitals', {
      metric_name: metric,
      metric_value: value,
      metric_rating: rating,
    });
  },

  // Custom performance metrics
  customMetric: (metricName: string, value: number, unit: string = 'ms') => {
    trackEvent('custom_performance_metric', {
      metric_name: metricName,
      metric_value: value,
      metric_unit: unit,
    });
  },
};

// Error tracking
export const trackError = (errorMessage: string, errorLocation: string, errorType: string = 'javascript') => {
  trackEvent('error', {
    error_message: errorMessage,
    error_location: errorLocation,
    error_type: errorType,
  });
};

// User journey tracking
export const trackUserJourney = {
  // Page view with journey context
  pageView: (pageName: string, journeyStep: string, timeOnPage?: number) => {
    trackEvent('page_view', {
      page_name: pageName,
      journey_step: journeyStep,
      time_on_page: timeOnPage,
    });
  },

  // User flow
  userFlow: (fromPage: string, toPage: string, action: string) => {
    trackEvent('user_flow', {
      from_page: fromPage,
      to_page: toPage,
      action: action,
    });
  },

  // Session duration
  sessionDuration: (duration: number) => {
    trackEvent('session_duration', {
      duration: duration,
    });
  },
};

export default {
  loadGoogleAnalytics,
  removeGoogleAnalytics,
  initGA, // Legacy - kept for backward compatibility
  trackPageView,
  trackEvent,
  trackEngagement,
  trackFormSubmission,
  trackButtonClick,
  trackSearch,
  trackDownload,
  trackVideoInteraction,
  trackPurchase,
  trackAppointmentBooking,
  setUserProperties,
  trackConversion,
  trackEnhancedEcommerce,
  trackPerformance,
  trackError,
  trackUserJourney,
};
